import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { SharedModule } from '../shared/shared.module';
import { AuthService } from '../services/auth.service';
import { FlagService } from '../shared/services/flag.service';
import { FlagType } from '../shared/utils/enums';
import { PASSWORD_RULES, STRONG_PASSWORD_PATTERN } from '../shared/utils/agency-rbac';

type PageState = 'loading' | 'form' | 'done' | 'error';

function passwordsMatch(group: AbstractControl): ValidationErrors | null {
  const password = group.get('password')?.value;
  const confirmation = group.get('confirmation')?.value;
  return password && confirmation && password !== confirmation ? { mismatch: true } : null;
}

/**
 * Public page reached from the invitation e-mail (/invitation?token=...): the collaborator checks
 * the invitation and chooses a password; the account then becomes active and can sign in
 * (password + e-mailed code).
 */
@Component({
  selector: 'app-invitation-accept',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink, SharedModule],
  templateUrl: './invitation-accept.component.html',
  styleUrl: './invitation-accept.component.scss',
})
export class InvitationAcceptComponent implements OnInit {
  state: PageState = 'loading';
  errorMessage = '';
  submitError = '';
  submitting = false;
  invitation: any = null;
  token = '';

  readonly rules = PASSWORD_RULES;

  form: FormGroup = this.fb.group(
    {
      password: ['', [Validators.required, Validators.pattern(STRONG_PASSWORD_PATTERN)]],
      confirmation: ['', Validators.required],
    },
    { validators: passwordsMatch }
  );

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private auth: AuthService,
    private flag: FlagService
  ) {}

  get password(): string {
    return this.form.get('password')?.value ?? '';
  }

  get mismatch(): boolean {
    return !!this.form.errors?.['mismatch'] && !!this.form.get('confirmation')?.value;
  }

  ngOnInit(): void {
    this.token = this.route.snapshot.queryParamMap.get('token') ?? '';
    if (!this.token) {
      this.fail("Lien d'invitation invalide.");
      return;
    }
    this.auth.getInvitation(this.token).subscribe({
      next: (invitation) => {
        this.invitation = invitation;
        this.state = 'form';
      },
      error: (err) => this.fail(err?.error?.message || "Lien d'invitation invalide ou expiré."),
    });
  }

  submit(): void {
    if (this.form.invalid || this.submitting) {
      this.form.markAllAsTouched();
      return;
    }
    this.submitting = true;
    this.submitError = '';
    const { password, confirmation } = this.form.value;
    this.auth.acceptInvitation(this.token, password, confirmation).subscribe({
      next: () => {
        this.submitting = false;
        this.state = 'done';
      },
      error: (err) => {
        this.submitting = false;
        const body = err?.error ?? {};
        if (body.code === 'INVITATION_EXPIRED' || body.code === 'INVITATION_INVALID' || body.code === 'INVITATION_USED') {
          this.fail(body.message);
          return;
        }
        this.submitError = body.message || "L'activation a échoué. Veuillez réessayer.";
      },
    });
  }

  goToLogin(): void {
    this.flag.displayFlag('Votre compte est activé. Connectez-vous avec votre e-mail et votre mot de passe.', FlagType.success);
    this.router.navigate(['/login']);
  }

  private fail(message: string): void {
    this.errorMessage = message;
    this.state = 'error';
  }
}
